import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  CardMedia,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  BoltOutlined, 
  CloudOutlined, 
  SettingsOutlined, 
  StorageOutlined,
  CheckCircleOutline,
  ArrowForward,
  SensorsOutlined,
  DataUsageOutlined,
  BarChartOutlined,
  DevicesOutlined
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getTranslation } from '../../i18n';
import ContactForm from '../contact/ContactForm';

const SensorInfrastructurePage = () => {
  const { language } = useSelector((state) => state.ui);
  const translations = getTranslation(language);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Benefits of our sensor infrastructure
  const benefits = [
    {
      icon: <BoltOutlined fontSize="large" />,
      title: translations.landing.sensorInfrastructure.page.benefits.items.lightningDetection.title,
      description: translations.landing.sensorInfrastructure.page.benefits.items.lightningDetection.description
    },
    {
      icon: <CloudOutlined fontSize="large" />,
      title: translations.landing.sensorInfrastructure.page.benefits.items.weatherMonitoring.title,
      description: translations.landing.sensorInfrastructure.page.benefits.items.weatherMonitoring.description
    },
    {
      icon: <SettingsOutlined fontSize="large" />,
      title: translations.landing.sensorInfrastructure.page.benefits.items.integration.title,
      description: translations.landing.sensorInfrastructure.page.benefits.items.integration.description
    },
    {
      icon: <StorageOutlined fontSize="large" />,
      title: translations.landing.sensorInfrastructure.page.benefits.items.dataCollection.title,
      description: translations.landing.sensorInfrastructure.page.benefits.items.dataCollection.description
    },
    {
      icon: <DataUsageOutlined fontSize="large" />,
      title: translations.landing.sensorInfrastructure.page.benefits.items.analytics.title,
      description: translations.landing.sensorInfrastructure.page.benefits.items.analytics.description
    },
    {
      icon: <DevicesOutlined fontSize="large" />,
      title: translations.landing.sensorInfrastructure.page.benefits.items.management.title,
      description: translations.landing.sensorInfrastructure.page.benefits.items.management.description
    }
  ];

  // Pricing plans data
  const pricingPlans = [
    {
      title: translations.landing.pricing.basic,
      description: translations.landing.pricing.basicDescription,
      color: '#1273D1',
      features: [
        'Real-time lightning monitoring',
        'Basic alerts',
        'Standard reports',
        'Email support'
      ]
    },
    {
      title: translations.landing.pricing.professional,
      description: translations.landing.pricing.professionalDescription,
      color: '#4CAF50',
      features: [
        'All Basic features',
        'Advanced analytics',
        'Custom alerts',
        'API access',
        'Priority support'
      ],
      highlighted: true
    },
    {
      title: translations.landing.pricing.enterprise,
      description: translations.landing.pricing.enterpriseDescription,
      color: '#9C27B0',
      features: [
        'All Professional features',
        'Custom integrations',
        'Dedicated account manager',
        'SLA guarantees',
        'On-site training'
      ]
    }
  ];

  // Use cases
  const useCases = [
    {
      title: translations.landing.sensorInfrastructure.page.useCases.items.energy.title,
      description: translations.landing.sensorInfrastructure.page.useCases.items.energy.description,
      image: "/images/landing/energy_sector.svg"
    },
    {
      title: translations.landing.sensorInfrastructure.page.useCases.items.aviation.title,
      description: translations.landing.sensorInfrastructure.page.useCases.items.aviation.description,
      image: "/images/landing/aviation.svg"
    },
    {
      title: translations.landing.sensorInfrastructure.page.useCases.items.agriculture.title,
      description: translations.landing.sensorInfrastructure.page.useCases.items.agriculture.description,
      image: "/images/landing/agriculture.svg"
    },
    {
      title: translations.landing.sensorInfrastructure.page.useCases.items.sports.title,
      description: translations.landing.sensorInfrastructure.page.useCases.items.sports.description,
      image: "/images/landing/sports.svg"
    }
  ];

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section */}
      <Box 
        sx={{
          background: 'linear-gradient(135deg, #1273D1 0%, #0E58A0 100%)',
          color: 'white',
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h2" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 800, 
                      mb: 2,
                      fontSize: { xs: '2rem', md: '3rem' }
                    }}
                  >
                    {translations.landing.sensorInfrastructure.page.hero.title}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 3, 
                      fontWeight: 400,
                      opacity: 0.9
                    }}
                  >
                    {translations.landing.sensorInfrastructure.page.hero.subtitle}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 4, 
                      maxWidth: 600,
                      opacity: 0.8
                    }}
                  >
                    {translations.landing.sensorInfrastructure.page.hero.description}
                  </Typography>
                  <Button 
                    variant="contained" 
                    size="large"
                    sx={{
                      backgroundColor: 'white',
                      color: '#1273D1',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.9)',
                      }
                    }}
                    onClick={() => {
                      const contactSection = document.getElementById('contact-section');
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                    }}
                    endIcon={<ArrowForward />}
                  >
                    {translations.landing.sensorInfrastructure.page.hero.contactButton}
                  </Button>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              <Box 
                component="img" 
                src="/images/landing/sensor_network.svg" 
                alt="Sensor Network Infrastructure"
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  height: 'auto',
                  borderRadius: 4,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              {translations.landing.sensorInfrastructure.page.benefits.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.sensorInfrastructure.page.benefits.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      height: '100%', 
                      p: 3,
                      borderRadius: 3,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ color: '#1273D1', mb: 2 }}>
                      {benefit.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {benefit.description}
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Process Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              {translations.landing.sensorInfrastructure.page.process.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.sensorInfrastructure.page.process.subtitle}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <List>
              <ListItem sx={{ pb: 4 }}>
                <ListItemIcon>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#1273D1',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h5">1</Typography>
                  </Paper>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {translations.landing.sensorInfrastructure.page.process.steps.step1.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1" color="textSecondary">
                      {translations.landing.sensorInfrastructure.page.process.steps.step1.description}
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ pb: 4 }}>
                <ListItemIcon>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#1273D1',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h5">2</Typography>
                  </Paper>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {translations.landing.sensorInfrastructure.page.process.steps.step2.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1" color="textSecondary">
                      {translations.landing.sensorInfrastructure.page.process.steps.step2.description}
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ pb: 4 }}>
                <ListItemIcon>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#1273D1',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h5">3</Typography>
                  </Paper>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {translations.landing.sensorInfrastructure.page.process.steps.step3.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1" color="textSecondary">
                      {translations.landing.sensorInfrastructure.page.process.steps.step3.description}
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ pb: 4 }}>
                <ListItemIcon>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#1273D1',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h5">4</Typography>
                  </Paper>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {translations.landing.sensorInfrastructure.page.process.steps.step4.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1" color="textSecondary">
                      {translations.landing.sensorInfrastructure.page.process.steps.step4.description}
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#1273D1',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h5">5</Typography>
                  </Paper>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {translations.landing.sensorInfrastructure.page.process.steps.step5.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1" color="textSecondary">
                      {translations.landing.sensorInfrastructure.page.process.steps.step5.description}
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </Container>
      </Box>

      {/* Use Cases Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              {translations.landing.sensorInfrastructure.page.useCases.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.sensorInfrastructure.page.useCases.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {useCases.map((useCase, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                  <Card 
                    elevation={3} 
                    sx={{ 
                      height: '100%',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={useCase.image}
                      alt={useCase.title}
                    />
                    <CardContent>
                      <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                        {useCase.title}
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        {useCase.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box 
                component="img" 
                src="/images/landing/why_choose_us.svg" 
                alt="Why Choose Us"
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  height: 'auto',
                  borderRadius: 4,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  mx: 'auto',
                  display: 'block'
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ 
                  mb: 4, 
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', md: '2.5rem' }
                }}
              >
                {translations.landing.sensorInfrastructure.page.whyUs.title}
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutline sx={{ color: '#1273D1' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translations.landing.sensorInfrastructure.page.whyUs.items.expertise.title}
                      </Typography>
                    }
                    secondary={translations.landing.sensorInfrastructure.page.whyUs.items.expertise.description}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutline sx={{ color: '#1273D1' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translations.landing.sensorInfrastructure.page.whyUs.items.trackRecord.title}
                      </Typography>
                    }
                    secondary={translations.landing.sensorInfrastructure.page.whyUs.items.trackRecord.description}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutline sx={{ color: '#1273D1' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translations.landing.sensorInfrastructure.page.whyUs.items.technology.title}
                      </Typography>
                    }
                    secondary={translations.landing.sensorInfrastructure.page.whyUs.items.technology.description}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutline sx={{ color: '#1273D1' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translations.landing.sensorInfrastructure.page.whyUs.items.approach.title}
                      </Typography>
                    }
                    secondary={translations.landing.sensorInfrastructure.page.whyUs.items.approach.description}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutline sx={{ color: '#1273D1' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translations.landing.sensorInfrastructure.page.whyUs.items.support.title}
                      </Typography>
                    }
                    secondary={translations.landing.sensorInfrastructure.page.whyUs.items.support.description}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              {translations.landing.pricing.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.pricing.description}
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                  <Paper 
                    elevation={plan.highlighted ? 8 : 2} 
                    sx={{ 
                      height: '100%', 
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: plan.highlighted ? `2px solid ${plan.color}` : 'none',
                      transform: plan.highlighted ? 'scale(1.05)' : 'none',
                      position: 'relative',
                      zIndex: plan.highlighted ? 2 : 1,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: plan.highlighted ? 'scale(1.08)' : 'scale(1.03)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    {plan.highlighted && (
                      <Box 
                        sx={{ 
                          backgroundColor: plan.color, 
                          color: 'white', 
                          py: 0.5, 
                          textAlign: 'center',
                          fontWeight: 600
                        }}
                      >
                        {translations.landing.pricing.mostPopular}
                      </Box>
                    )}
                    <Box sx={{ p: 4 }}>
                      <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 700, color: plan.color }}>
                        {plan.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 3, height: 40 }}>
                        {plan.description}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 4, fontWeight: 600 }}>
                        {translations.landing.pricing.contact}
                      </Typography>
                      <Divider sx={{ my: 3 }} />
                      <Box sx={{ mb: 3 }}>
                        {plan.features.map((feature, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CheckCircleOutline sx={{ color: plan.color, mr: 1, fontSize: 20 }} />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Button 
                        variant={plan.highlighted ? "contained" : "outlined"} 
                        fullWidth
                        component={Link}
                        to="/contact"
                        size="large"
                        sx={{ 
                          mt: 2,
                          backgroundColor: plan.highlighted ? plan.color : 'transparent',
                          borderColor: plan.color,
                          color: plan.highlighted ? 'white' : plan.color,
                          '&:hover': {
                            backgroundColor: plan.highlighted ? plan.color : 'rgba(0,0,0,0.04)',
                            borderColor: plan.color,
                          }
                        }}
                      >
                        {translations.landing.pricing.contactSales}
                      </Button>
                    </Box>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact-section" sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              {translations.landing.sensorInfrastructure.page.contact.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.sensorInfrastructure.page.contact.subtitle}
            </Typography>
          </Box>

          <ContactForm />

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="body1">
              {translations.landing.sensorInfrastructure.page.contact.email} <strong>{translations.landing.sensorInfrastructure.page.contact.emailAddress}</strong>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SensorInfrastructurePage;
